import { useEffect, useRef, useState } from 'react';
import type { ScrollInput, ViewState } from '../../viewer/ViewportEngine';
import type { ReviewScan, ScanSnapshot } from '../../viewer/scan/ReviewScan';
import { paintScanMasks, type ScanMasks } from '../../viewer/scan/paintMasks';
import { useScanSnapshot } from '../../viewer/scan/useScanSnapshot';
import { pinchFactor } from '../../viewer/zoomLimits';

interface Props {
  /** Resolves the whole-slide image — the server's pregenerated thumbnail — or
   *  null, in which case there is no minimap. A plain cacheable URL, not an
   *  object URL: nothing here allocates or has to revoke it. */
  getThumbnail: () => Promise<{ url: string; aspect: number } | null>;
  viewRect: ViewState['viewRect'];
  rotationDeg: number;
  /** Centre the view on a slide fraction; `animate` false while dragging. */
  onNavigate: (x: number, y: number, animate: boolean) => void;
  /** Zoom the view by `factor` about a slide fraction (a pinch). */
  onZoomAt: (factor: number, x: number, y: number) => void;
  /** Zoom the view from a plain scroll about a slide fraction (the wheel). */
  onScrollAt: (e: ScrollInput, x: number, y: number) => void;
  /** The open review scan: its plan and coverage are drawn over the thumbnail. */
  scan?: ReviewScan | null;
  /** The image is shown inverted (an X-ray's Invert): the thumbnail is turned over with it. */
  inverted?: boolean;
}

const BASE_W = 140;

/** Whether an event is over a minimap, which zooms about what it points at: the
 *  page-level pinch handlers leave those events to it. */
export const isOverMinimap = (e: Event): boolean =>
  e.target instanceof Element && !!e.target.closest('[data-minimap]');

// A self-contained overview minimap: whole-slide thumbnail + a live view-rectangle,
// resizable from the bottom-right, press or drag to navigate, wheel or pinch to
// zoom. Always upright and corner-anchored (it's a plain DOM element, unlike DMV's
// rotating overview).
export function OverviewMinimap({ getThumbnail, viewRect, rotationDeg, onNavigate, onZoomAt, onScrollAt, scan = null, inverted = false }: Props) {
  const [thumb, setThumb] = useState<{ url: string; aspect: number } | null>(null);
  // Refreshed when either mask changes, at most once a frame (the controller
  // emits no faster).
  const masks = useScanSnapshot(scan, masksOf, sameMasks);

  useEffect(() => {
    let alive = true;
    getThumbnail().then((t) => {
      if (alive) setThumb(t);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!thumb) return null;

  return (
    <Minimap
      thumb={thumb}
      viewRect={viewRect}
      rotationDeg={rotationDeg}
      onNavigate={onNavigate}
      onZoomAt={onZoomAt}
      onScrollAt={onScrollAt}
      masks={masks}
      inverted={inverted}
    />
  );
}

const masksOf = (s: ScanSnapshot): ScanMasks => ({
  plan: s.plan,
  coverage: s.coverage,
  version: (s.plan?.version ?? 0) + (s.coverage?.version ?? 0),
});
const sameMasks = (a: ScanMasks, b: ScanMasks) =>
  a.plan === b.plan && a.coverage === b.coverage && a.version === b.version;

/** The plan and the covered area over the thumbnail, inside the rotated
 *  content box so rotation and resizing come for free. */
function ScanCanvas({ masks, width, height }: { masks: ScanMasks; width: number; height: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current) paintScanMasks(ref.current, masks, width, height);
  }, [masks, width, height]);
  return (
    <canvas
      ref={ref}
      aria-hidden
      data-scan-canvas
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Whether a slide fraction lies inside the view box, which is upright on
 * SCREEN: turned by -rotationDeg inside the content (contentW x contentH px)
 * that turns with the view. Measured in content pixels, where the two axes
 * share a scale.
 */
export function insideView(
  view: { x: number; y: number; w: number; h: number },
  px: number,
  py: number,
  contentW: number,
  contentH: number,
  rotationDeg: number,
): boolean {
  const a = (rotationDeg * Math.PI) / 180;
  const dx = (px - view.x) * contentW;
  const dy = (py - view.y) * contentH;
  // Into the box's own axes: the view's turn, which the box undoes.
  const lx = Math.cos(a) * dx - Math.sin(a) * dy;
  const ly = Math.sin(a) * dx + Math.cos(a) * dy;
  return Math.abs(lx) <= (view.w * contentW) / 2 && Math.abs(ly) <= (view.h * contentH) / 2;
}

const MIN_H = 50;
const MAX_H = 240;

const PANEL: React.CSSProperties = {
  pointerEvents: 'auto',
  background: 'rgba(0,0,0,0.6)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6,
  padding: 3,
  width: 'fit-content',
};

function Minimap({
  thumb,
  viewRect,
  rotationDeg,
  onNavigate,
  onZoomAt,
  onScrollAt,
  masks = null,
  inverted = false,
}: {
  thumb: { url: string; aspect: number };
  viewRect: ViewState['viewRect'];
  rotationDeg: number;
  onNavigate: (x: number, y: number, animate: boolean) => void;
  onZoomAt: (factor: number, x: number, y: number) => void;
  onScrollAt: (e: ScrollInput, x: number, y: number) => void;
  masks?: ScanMasks | null;
  inverted?: boolean;
}) {
  // Height is the constant measure: rotating keeps the container height fixed and
  // the width grows/shrinks to the right. The rotated content is scaled so its
  // rotated bounding-box height equals the container height.
  const [boxH, setBoxH] = useState(Math.round(BASE_W / thumb.aspect));
  const [collapsed, setCollapsed] = useState(false);

  const rad = (rotationDeg * Math.PI) / 180;
  const ac = Math.abs(Math.cos(rad));
  const as = Math.abs(Math.sin(rad));
  const contentH = boxH / (thumb.aspect * as + ac); // so rotated bbox height === boxH
  const contentW = contentH * thumb.aspect;
  const containerW = Math.round(contentW * ac + contentH * as);

  // Maps a pointer position in the (rotated) container back to a slide fraction.
  const toFraction = (el: HTMLElement, clientX: number, clientY: number): [number, number] => {
    const r = el.getBoundingClientRect();
    const u = clientX - r.left - r.width / 2;
    const v = clientY - r.top - r.height / 2;
    const a = -rad;
    const cx = u * Math.cos(a) - v * Math.sin(a);
    const cy = u * Math.sin(a) + v * Math.cos(a);
    return [cx / contentW + 0.5, cy / contentH + 0.5];
  };

  // The wheel and a pinch zoom the view as they do over the slide itself, about
  // the spot under the pointer: the view box shrinks toward it, or grows away.
  // A native listener, because React's wheel listeners are passive and a pinch
  // must not reach the browser's page zoom.
  const boxRef = useRef<HTMLDivElement>(null);
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.deltaY) return;
    const [x, y] = toFraction(e.currentTarget as HTMLElement, e.clientX, e.clientY);
    // A pinch arrives as Ctrl+wheel; a plain scroll is the view's scroll
    // gesture, as over the slide.
    if (e.ctrlKey) onZoomAt(pinchFactor(e), clamp01(x), clamp01(y));
    else onScrollAt(e, clamp01(x), clamp01(y));
  };
  const onWheelRef = useRef(onWheel);
  onWheelRef.current = onWheel;
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const listener = (e: WheelEvent) => onWheelRef.current(e);
    el.addEventListener('wheel', listener, { passive: false });
    return () => el.removeEventListener('wheel', listener);
  }, [collapsed]);

  // Press to go there, drag to move the view with the pointer. Pressing inside
  // the view box grabs it where it was pressed rather than re-centring it under
  // the pointer, so a small drag is a small move. While dragging the view
  // follows at once (no easing), at most once per frame.
  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const [px, py] = toFraction(el, e.clientX, e.clientY);
    const inside = !!viewRect && insideView(viewRect, px, py, contentW, contentH, rotationDeg);
    const ox = inside && viewRect ? viewRect.x - px : 0;
    const oy = inside && viewRect ? viewRect.y - py : 0;
    if (!inside) onNavigate(clamp01(px), clamp01(py), true);
    let frame = 0;
    let next: [number, number] | null = null;
    const onMove = (ev: PointerEvent) => {
      const [x, y] = toFraction(el, ev.clientX, ev.clientY);
      next = [clamp01(x + ox), clamp01(y + oy)];
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (next) onNavigate(next[0], next[1], false);
        });
      }
    };
    const onUp = () => {
      if (frame) cancelAnimationFrame(frame);
      if (next) onNavigate(next[0], next[1], false);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  };

  // Resize from the right/bottom borders (or corner). Height is the source of
  // truth; a right-border drag is mapped to the equivalent height change so the
  // aspect stays locked and rotation keeps the height constant.
  const startResize = (axis: 'x' | 'y' | 'xy') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startH = boxH;
    const startCW = containerW;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const fromX = startH + (dx * startH) / Math.max(1, startCW);
      const fromY = startH + dy;
      const next = axis === 'x' ? fromX : axis === 'y' ? fromY : Math.max(fromX, fromY);
      setBoxH(Math.max(MIN_H, Math.min(MAX_H, next)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        title="Show overview"
        onClick={() => setCollapsed(false)}
        style={{ ...PANEL, padding: '4px 6px', color: '#fff', cursor: 'pointer', lineHeight: 1 }}
      >
        ›
      </button>
    );
  }

  // The screen, which never turns: inside the turned content it takes the
  // opposite turn, so it stays upright while the slide turns under it.
  const rect = viewRect && {
    left: (viewRect.x - viewRect.w / 2) * contentW,
    top: (viewRect.y - viewRect.h / 2) * contentH,
    width: viewRect.w * contentW,
    height: viewRect.h * contentH,
    transform: rotationDeg ? `rotate(${-rotationDeg}deg)` : undefined,
  };

  return (
    <div style={PANEL}>
      <div
        ref={boxRef}
        data-minimap
        onPointerDown={startDrag}
        style={{
          position: 'relative',
          width: containerW,
          height: boxH,
          overflow: 'hidden',
          borderRadius: 4,
          cursor: 'crosshair',
          touchAction: 'none', // a finger drag moves the view, not the page
        }}
      >
        {/* Rotated content: thumbnail + view-rectangle, centered in the box */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: contentW,
            height: contentH,
            transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
          }}
        >
          <img
            src={thumb.url}
            alt="overview"
            draggable={false}
            // An X-ray's thumbnail is 8-bit grey: turned over, it is the inverted picture exactly.
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', filter: inverted ? 'invert(1)' : undefined }}
          />
          {masks && <ScanCanvas masks={masks} width={contentW} height={contentH} />}
          {rect && (
            <div
              style={{
                position: 'absolute',
                ...rect,
                // White, not blue (user, 2026-09-26); the dark outline keeps it on white glass.
                border: '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        {/* Hide button — chevron on the right border */}
        <button
          type="button"
          title="Hide overview"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed(true);
          }}
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            borderRadius: 3,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ‹
        </button>

        {/* Right-edge resize (width) */}
        <div
          onPointerDown={startResize('x')}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', top: 20, bottom: 16, right: 0, width: 6, cursor: 'ew-resize' }}
        />
        {/* Bottom-edge resize (height) */}
        <div
          onPointerDown={startResize('y')}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', left: 0, right: 16, bottom: 0, height: 6, cursor: 'ns-resize' }}
        />
        {/* Bottom-right corner resize */}
        <div
          onPointerDown={startResize('xy')}
          onClick={(e) => e.stopPropagation()}
          title="Resize"
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 16,
            height: 16,
            cursor: 'nwse-resize',
            background:
              'linear-gradient(135deg, transparent 0 50%, rgba(255,255,255,0.85) 50% 60%, transparent 60% 70%, rgba(255,255,255,0.85) 70% 80%, transparent 80%)',
          }}
        />
      </div>
    </div>
  );
}
